/**
 * ProfileStep - Profile setup step of onboarding
 * PRD: PRD-onboarding-organisateur.md
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Button, Icon } from '../../../../pages/DesignSystem';
import { OrganizerProfile } from '../../../../types/organizer.types';
import { useGoogleMaps } from '../../../../contexts/GoogleMapsContext';
import { cloudinaryService } from '../../../../services/cloudinary.service';
import { toast } from 'react-hot-toast';
import { PlaceAutocomplete, PlaceAutocompleteResult } from '../../../shared/PlaceAutocomplete';

interface ProfileStepProps {
  profile: OrganizerProfile | null;
  onSubmit: (data: Partial<OrganizerProfile>) => Promise<void>;
  onFormDataChange?: (data: Partial<OrganizerProfile>) => void;
  isSubmitting: boolean;
}

interface FormData {
  organizer: string;
  subtitle: string;
  organizerImage: string;
  location: string;
  locationName: string;
  latitude: number | null;
  longitude: number | null;
}

const ProfileStep: React.FC<ProfileStepProps> = ({
  profile,
  onSubmit,
  onFormDataChange,
  isSubmitting,
}) => {
  const { isLoaded: isMapsLoaded, loadError: mapsError, requestLoad } = useGoogleMaps();

  // Trigger le chargement de Google Maps quand le step monte
  useEffect(() => {
    requestLoad();
  }, [requestLoad]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<FormData>({
    organizer: profile?.organizer || '',
    subtitle: profile?.subtitle || '',
    organizerImage: profile?.organizerImage || '',
    location: profile?.location || '',
    locationName: profile?.locationName || '',
    latitude: profile?.latitude || null,
    longitude: profile?.longitude || null,
  });

  const [isUploading, setIsUploading] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  // Notify parent of form data changes (for live preview)
  useEffect(() => {
    if (onFormDataChange) {
      onFormDataChange({
        organizer: formData.organizer,
        subtitle: formData.subtitle,
        organizerImage: formData.organizerImage,
        location: formData.location,
        locationName: formData.locationName,
        latitude: formData.latitude ?? undefined,
        longitude: formData.longitude ?? undefined,
      });
    }
  }, [formData, onFormDataChange]);

  // Handle place selection from PlaceAutocomplete
  const handlePlaceSelect = useCallback((result: PlaceAutocompleteResult) => {
    // Extract city/area name for display (preserve existing logic)
    let locationName = '';
    if (result.addressComponents) {
      const city = result.addressComponents.find((c) =>
        c.types.includes('locality')
      );
      const area = result.addressComponents.find((c) =>
        c.types.includes('sublocality')
      );
      // New API uses longText instead of long_name
      locationName = city?.longText || area?.longText || '';
    }

    setFormData((prev) => ({
      ...prev,
      location: result.formattedAddress || '',
      locationName: locationName || result.displayName || '',
      latitude: result.location?.lat ?? null,
      longitude: result.location?.lng ?? null,
    }));

    setErrors((prev) => ({ ...prev, location: undefined }));
  }, []);

  // Handle name change
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, organizer: value }));
    if (value.trim()) {
      setErrors((prev) => ({ ...prev, organizer: undefined }));
    }
  };

  // Handle subtitle/bio change
  const handleSubtitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, subtitle: value }));
  };

  // Handle image upload using Cloudinary
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB like in ProfileEditForm)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setIsUploading(true);

    // Create temporary URL for preview
    const tempUrl = URL.createObjectURL(file);
    setFormData((prev) => ({
      ...prev,
      organizerImage: tempUrl,
    }));

    try {
      const cloudinaryUrl = await cloudinaryService.uploadImage(file);

      if (cloudinaryUrl) {
        setFormData((prev) => ({
          ...prev,
          organizerImage: cloudinaryUrl,
        }));
        setErrors((prev) => ({ ...prev, organizerImage: undefined }));
        toast.success('Image uploaded!');
      } else {
        // Revert to empty if upload failed
        setFormData((prev) => ({
          ...prev,
          organizerImage: '',
        }));
        toast.error('Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setFormData((prev) => ({
        ...prev,
        organizerImage: '',
      }));
      // 🔧 FIX (item 6 - PRD robustesse réseau faible): message lisible plutôt
      // qu'une erreur technique brute, avec un cas dédié pour le timeout.
      const isTimeout = error instanceof DOMException && error.name === 'AbortError';
      toast.error(isTimeout ? 'Upload timed out — check your connection and try again' : 'Failed to upload image');
    } finally {
      URL.revokeObjectURL(tempUrl);
      setIsUploading(false);
    }
  };

  // Validate form - only organizer name is required
  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.organizer.trim()) {
      newErrors.organizer = 'Name is required';
    } else if (formData.organizer.trim().length < 2) {
      newErrors.organizer = 'Name must be at least 2 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    await onSubmit({
      organizer: formData.organizer.trim(),
      subtitle: formData.subtitle.trim() || undefined,
      organizerImage: formData.organizerImage || undefined,
      location: formData.location || undefined,
      locationName: formData.locationName || undefined,
      latitude: formData.latitude ?? undefined,
      longitude: formData.longitude ?? undefined,
    });
  };

  // Only organizer name is required
  const isFormValid = formData.organizer.trim().length >= 2;

  return (
    <div className="onboarding-step onboarding-step--profile">
      <div className="onboarding-step__content">
        <h1 className="onboarding-step__title">Let's set up your profile! 📝</h1>
        <p className="onboarding-step__description">
          Only your name is required—you can add the rest anytime! No pressure 😊
        </p>
      </div>

      <form className="onboarding-step__form" onSubmit={handleSubmit}>
        {/* Name - Required */}
        <div className="onboarding-form__field">
          <label htmlFor="organizer" className="onboarding-form__label">
            Name <span className="required">*</span>
          </label>
          <input
            id="organizer"
            type="text"
            className={`onboarding-form__input ${errors.organizer ? 'onboarding-form__input--error' : ''}`}
            placeholder="Your name or venue name"
            value={formData.organizer}
            onChange={handleNameChange}
            maxLength={100}
          />
          <span className="onboarding-form__hint">
            This can be your personal name or your venue/organization name
          </span>
          {errors.organizer && (
            <span className="onboarding-form__error">{errors.organizer}</span>
          )}
        </div>

        {/* Bio/Tagline - Optional */}
        <div className="onboarding-form__field">
          <label htmlFor="subtitle" className="onboarding-form__label">
            Bio
            <span className="onboarding-form__optional">Optional</span>
          </label>
          <input
            id="subtitle"
            type="text"
            className="onboarding-form__input"
            placeholder="e.g. The best Latin parties in Paris"
            value={formData.subtitle}
            onChange={handleSubtitleChange}
            maxLength={100}
          />
          <span className="onboarding-form__hint">
            A short tagline like your Instagram bio - show your vibe!
          </span>
        </div>

        {/* Location - Optional */}
        <div className="onboarding-form__field">
          <label htmlFor="location" className="onboarding-form__label">
            Your Location
            <span className="onboarding-form__optional">Optional</span>
          </label>
          {mapsError ? (
            <div className="onboarding-form__maps-error">
              <Icon name="AlertTriangle" size={16} />
              Location search unavailable. Please try again later.
            </div>
          ) : (
            <PlaceAutocomplete
              placeholder="Search for your address or business"
              value={formData.location}
              onChange={(value) => setFormData((prev) => ({ ...prev, location: value }))}
              onPlaceSelect={handlePlaceSelect}
              error={errors.location}
              disabled={!isMapsLoaded || !!mapsError}
              className="onboarding-form__input"
            />
          )}
          {errors.location && (
            <span className="onboarding-form__error">{errors.location}</span>
          )}
          <span className="onboarding-form__hint">
            Your default spot for events 📍 You can change it anytime—including when creating an event!
          </span>
        </div>

        {/* Profile Photo - Optional */}
        <div className="onboarding-form__field">
          <label className="onboarding-form__label">
            Profile Photo
            <span className="onboarding-form__optional">Optional</span>
          </label>
          <div className="onboarding-form__photo-upload">
            {formData.organizerImage ? (
              <div className="onboarding-form__photo-preview">
                <img
                  src={formData.organizerImage}
                  alt="Profile preview"
                  className="onboarding-form__photo-img"
                />
                <button
                  type="button"
                  className="onboarding-form__photo-remove"
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, organizerImage: '' }))
                  }
                  aria-label="Remove photo"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="onboarding-form__photo-button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                {isUploading ? (
                  <span className="onboarding-form__photo-loading">
                    <Icon name="Loader" size={24} />
                    Uploading...
                  </span>
                ) : (
                  <>
                    <Icon name="Camera" size={24} />
                    <span>Add Photo</span>
                  </>
                )}
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="visually-hidden"
            />
          </div>
          <span className="onboarding-form__hint">
            Makes your profile pop! ✨ Tourists love a friendly face or place
          </span>
        </div>

        {/* Submit */}
        <div className="onboarding-step__actions">
          <Button
            variant="primary"
            size="lg"
            type="submit"
            disabled={!isFormValid || isSubmitting || isUploading}
            className="onboarding-step__cta"
          >
            {isSubmitting ? 'Saving...' : 'Continue'}
            <Icon name="ArrowRight" size={18} />
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ProfileStep;
